import { useCallback, useEffect, useState } from 'react';
import { getUsersForClient } from '../services/api';
import { listenToTender, subscribeToTenderSections } from '../services/tenderCoreService';
import { subscribeToTenderContextDocs } from '../services/tenderFileService';
import { getUsersByClient, getUsersByTeam } from '../services/userService';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { User } from '../types/auth';
import type { SectionUserRef, Tender } from '../types/tender';
import { markTenderVisited } from '../utils/recentTenders';

export const useTenderCoreData = (
  tenderId: string | undefined,
  account: (User & { clientId?: string; teamId?: string }) | null,
  hasPermission: (perm: string) => boolean,
  showSnackbar: (msg: string, type: 'success' | 'error') => void,
  navigate: (path: string) => void
) => {
  const [tender, setTender] = useState<Tender | null>(null);
  const [hasAddedContextFiles, setHasAddedContextFiles] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [contributors, setContributors] = useState<SectionUserRef[]>([]);
  const [sectionsCount, setSectionsCount] = useState<number>(0);
  const [reviewedSectionsCount, setReviewedSectionsCount] = useState<number>(0);

  // 1. Listen to Tender
  useEffect(() => {
    if (!tenderId || !account?.clientId || !account?.uid) {
      return;
    }

    const unsubscribe = listenToTender(
      tenderId,
      (updatedTender) => {
        setTender(updatedTender);
        if (updatedTender.id && account.uid) {
          markTenderVisited(account.uid, updatedTender.id);
        }
      },
      (error) => {
        // biome-ignore lint/suspicious/noConsole: Expected error logging
        console.error('Firebase snapshot error on tenderRef:', error);
        showSnackbar('Error fetching tender details', 'error');
        navigate('/dashboard');
      }
    );

    return () => unsubscribe();
  }, [tenderId, account?.clientId, account?.uid, navigate, showSnackbar]);

  // Helper for tender permissions
  const checkTenderPerm = useCallback(
    (perm: string) => {
      if (hasPermission(perm)) {
        return true;
      }
      const userTenderPermissions = tender?.memberPermissions?.[account?.uid || ''];
      if (!userTenderPermissions) {
        return false;
      }
      if (Array.isArray(userTenderPermissions)) {
        return userTenderPermissions.includes(perm);
      }
      if (typeof userTenderPermissions === 'object' && userTenderPermissions !== null) {
        return !!(userTenderPermissions as Record<string, boolean>)[perm];
      }
      return false;
    },
    [hasPermission, tender?.memberPermissions, account?.uid]
  );

  // 2. Context Files Listener
  useEffect(() => {
    if (!tenderId || !account || !tender) {
      return;
    }

    const hasViewAllFiles = Boolean(
      tender?.ownerId === account?.uid || checkTenderPerm(PERMISSIONS.TENDER_FILES_VIEW_CLIENT)
    );

    const unsubscribe = subscribeToTenderContextDocs(
      tenderId,
      account.uid,
      hasViewAllFiles,
      (docs) => {
        const hasAdded = docs.some((d) => !String(d.originalFilename)?.includes('notice_only'));
        setHasAddedContextFiles(hasAdded);
      },
      (error) => {
        // biome-ignore lint/suspicious/noConsole: Expected error logging
        console.error('Firebase snapshot error on tender_docs for context status:', error);
      }
    );
    return () => unsubscribe();
  }, [
    tenderId,
    account?.uid,
    tender?.memberPermissions,
    tender?.ownerId,
    tender,
    account,
    checkTenderPerm,
  ]);

  // 3. Fetch Users
  useEffect(() => {
    if (!account?.clientId) {
      return;
    }

    if (hasPermission(PERMISSIONS.USER_VIEW)) {
      getUsersForClient(account.clientId)
        .then((res) => {
          if (res.success && res.users) {
            setUsers(res.users);
          }
        })
        .catch(() => {
          /* ignore */
        });
    } else {
      const fetchUsers = async () => {
        try {
          if (account.role === 'team_lead' && account.teamId) {
            const teamUsers = await getUsersByTeam(account.teamId, account.clientId as string);
            setUsers(teamUsers);
          } else {
            const clientUsers = await getUsersByClient(account.clientId as string);
            setUsers(clientUsers);
          }
        } catch (_err) {
          /* ignore */
        }
      };
      fetchUsers();
    }
  }, [account?.clientId, account?.teamId, account?.role, hasPermission]);

  // 4. Listen to Sections
  useEffect(() => {
    if (!account?.uid || !tenderId || !account?.clientId || !tender) {
      return;
    }

    const isOwner = tender.ownerId === account.uid;
    const hasFullAccess = Boolean(isOwner || checkTenderPerm(PERMISSIONS.TENDER_FULL_ACCESS));
    const hasViewAllSections = Boolean(
      hasFullAccess || checkTenderPerm(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT)
    );

    const unsubscribe = subscribeToTenderSections(
      tenderId,
      account.uid,
      hasViewAllSections,
      (docs) => {
        setSectionsCount(docs.length);
        const reviewed = docs.filter((d) => d.status === 'reviewed').length;
        setReviewedSectionsCount(reviewed);

        const uniqueContributors = new Map<string, SectionUserRef>();
        for (const d of docs) {
          if (d.lastEditedBy) {
            uniqueContributors.set(d.lastEditedBy.uid, d.lastEditedBy);
          }
          if (d.assignedTo) {
            uniqueContributors.set(d.assignedTo.uid, d.assignedTo);
          }
        }
        setContributors(Array.from(uniqueContributors.values()));
      },
      (error: Error & { code?: string }) => {
        // biome-ignore lint/suspicious/noConsole: Expected error logging
        console.error('Firebase snapshot error on sections:', error);
        if (error.code === 'permission-denied') {
          showSnackbar('Permission denied loading sections. Please refresh.', 'error');
        }
      }
    );

    return () => unsubscribe();
  }, [
    tenderId,
    account?.uid,
    account?.clientId,
    tender?.memberPermissions,
    showSnackbar,
    tender?.ownerId,
    tender,
    checkTenderPerm,
  ]);

  return {
    tender,
    setTender,
    hasAddedContextFiles,
    setHasAddedContextFiles,
    users,
    contributors,
    sectionsCount,
    reviewedSectionsCount,
  };
};
